import { useMemo, useState } from "react";
import { Line } from "react-chartjs-2";
import Card from "@mui/material/Card";
import IconButton from "@mui/material/IconButton";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

const DEBUG_CHARTS = false;

function SignupTrends({ data }) {
  const months = useMemo(() => ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"], []);
  const stats = data?.earnings || data || {};
  const byYear = stats.byYear || {};

  const currentYear = new Date().getFullYear();
  const years = useMemo(() => Object.keys(byYear).map(Number).filter(Number.isFinite).sort((a,b)=>a-b), [byYear]);
  const minYear = years.length ? years[0] : currentYear;
  const maxYear = years.length ? years[years.length - 1] : currentYear;

  const [year, setYear] = useState(() => (years.includes(currentYear) ? currentYear : maxYear));
  const bucket = byYear?.[year] || byYear?.[String(year)] || null;

  const normInt12 = (arr) => Array.from({ length: 12 }, (_, i) => Math.max(0, Math.floor(Number(arr?.[i] ?? 0) || 0)));

  const monthly = normInt12(bucket?.monthlySignups);
  const annual = normInt12(bucket?.annualSignups);
  const total = months.map((_, i) => monthly[i] + annual[i]);

  if (DEBUG_CHARTS) {
    console.log("=== CHART DEBUG: SignupTrends ===");
    console.log("byYear keys:", Object.keys(byYear || {}));
    console.log("selected year:", year, "bucket?", !!bucket);
    console.log("monthly sum:", monthly.reduce((a,b)=>a+b,0), "annual sum:", annual.reduce((a,b)=>a+b,0));
  }

  const hasAnyData = monthly.some((n) => n > 0) || annual.some((n) => n > 0);



  return (
    <Card>
      <MDBox p={3}>
        <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <MDTypography variant="h6">Signup Trends</MDTypography>

          <MDBox display="flex" alignItems="center" gap={1}>
            <IconButton size="small" onClick={() => setYear((y) => Math.max(minYear, y - 1))} disabled={year <= minYear}>
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <MDTypography variant="button" fontWeight="medium">{year}</MDTypography>
            <IconButton size="small" onClick={() => setYear((y) => Math.min(maxYear, y + 1))} disabled={year >= maxYear}>
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </MDBox>
        </MDBox>

        <Line
          data={{
            labels: months,
            datasets: [
              { label: "Monthly Signups", data: monthly, borderColor: "rgb(53, 162, 235)", tension: 0.2 },
              { label: "Annual Signups", data: annual, borderColor: "rgb(153, 102, 255)", tension: 0.2 },
              { label: "Total Signups", data: total, borderColor: "rgb(255, 159, 64)", tension: 0.2 },
            ],
          }}
          options={{
            responsive: true,
            maintainAspectRatio: true,
            scales: { y: { beginAtZero: true, ticks: { stepSize: 1 } } },
            plugins: { legend: { position: "bottom" } },
          }}
        />
      </MDBox>
    </Card>
  );
}

export default SignupTrends;